import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { EChartsType } from 'echarts';
import { Layout } from 'tea-component';
import { connect, useIntl } from 'umi';
import classnames from 'classnames';
import Loading from '@/pages/dashboard/components/Loading';
import { STH_CONFIG } from '../_config/constants';
import StudioCard from './components/StudioCard';
import StudioMap from './components/StudioMap';
import ConnectionLine from './components/ConnectionLine';
import { STUDIO_JSON, IStudio } from './const/studio';
import './index.scss';
import { buriedPointPage } from '@home/utils/buriedPoint';

const { Content } = Layout;

interface IWorldMapProps {
  gameCode: string;
}

interface IStudioDistribution {
  topCards: IStudio[];
  leftCards: IStudio[];
  rightCards: IStudio[];
  bottomCards: IStudio[];
}

/**
 * Calculate the number of cards to display at the top
 * based on container width
 */
const calculateTopCount = (containerWidth: number): number => {
  const { CARD_WIDTH, CARD_GAP, MIN_CARDS_PER_ROW } = STH_CONFIG.UI;
  const topCount = Math.floor(
    containerWidth / (CARD_WIDTH + CARD_GAP),
  );
  return Math.max(topCount, MIN_CARDS_PER_ROW);
};

const getDistribution = (
  studios: IStudio[],
  containerWidth: number,
): IStudioDistribution => {
  const topCount = calculateTopCount(containerWidth);
  const topCards = studios.slice(0, topCount);

  let remainingCards = studios.slice(topCount);

  const maxCardsPerSide = 6;
  const maxSideTotal = maxCardsPerSide * 2;
  const bottomMaxCards = Math.max(topCount - 2, 0);

  const leftCards: IStudio[] = [];
  const rightCards: IStudio[] = [];
  let bottomCards: IStudio[] = [];

  // --- fill left / right first ---
  const sideCards = remainingCards.slice(0, maxSideTotal);
  remainingCards = remainingCards.slice(maxSideTotal);

  sideCards.forEach((card, index) => {
    if (index % 2 === 0) {
      leftCards.push(card);
    } else {
      rightCards.push(card);
    }
  });

  // --- overflow goes to bottom ---
  if (remainingCards.length > 0) {
    bottomCards = remainingCards.slice(0, bottomMaxCards);
    remainingCards = remainingCards.slice(bottomMaxCards);
  }

  // --- extra cards go back to left / right ---
  remainingCards.forEach((card, index) => {
    if (index % 2 === 0) {
      leftCards.push(card);
    } else {
      rightCards.push(card);
    }
  });

  type TSource = 'right' | 'left' | 'bottom' | null;

  let specialStudio: IStudio | undefined;
  let source: TSource = null;
  if (bottomCards.length > 0) {
    const specialStudioName = 'Yongxing';
    const bottomIndex = bottomCards.findIndex(c => c.name === specialStudioName);
    if (bottomIndex !== -1) {
      [specialStudio] = bottomCards.splice(bottomIndex, 1);
      source = 'bottom';
    } else {
      const leftIndex = leftCards.findIndex(c => c.name === specialStudioName);
      if (leftIndex !== -1) {
        [specialStudio] = leftCards.splice(leftIndex, 1);
        source = 'left';
      } else {
        const rightIndex = rightCards.findIndex(c => c.name === specialStudioName);
        if (rightIndex !== -1) {
          [specialStudio] = rightCards.splice(rightIndex, 1);
          source = 'right';
        }
      }
    }

    if (specialStudio) {
      bottomCards.push(specialStudio);
    }
    if (specialStudio && source && source !== 'bottom') {
      const movedCard = bottomCards.shift();
      if (movedCard) {
        if (source === 'left') {
          leftCards.push(movedCard);
        } else if (source === 'right') {
          rightCards.push(movedCard);
        }
      }
    }
  }

  // --- swap cards based on position preference ---
  // Find cards in leftCards that need to be on the right side
  const leftCardsNeedingRight = leftCards
    .map((card, index) => ({ card, index }))
    .filter(({ card }) => card.position === 'right');

  // Swap each card that needs right position with a card from rightCards
  for (const { card: cardToMove, index: leftIndex } of leftCardsNeedingRight) {
    // Find a card in rightCards without position preference to swap
    leftCards[leftIndex] = rightCards[leftIndex];
    rightCards[leftIndex] = cardToMove;
  }

  return {
    topCards,
    leftCards,
    rightCards,
    bottomCards,
  };
};

/**
 * WorldMap page component
 * Display global studio distribution
 */
const WorldMap: React.FC<IWorldMapProps> = () => {
  const { formatMessage } = useIntl();
  const [hoveredStudioName, setHoveredStudioName] = useState<string | string[] | null>(null);
  const [selectedStudioNames, setSelectedStudioNames] = useState<string[] | null>(null);
  const [chartInstance, setChartInstance] = useState<EChartsType | null>(null);
  const [loading, setLoading] = useState(true);
  // const [studioData, setStudioData] = useState<IStudio[]>(STUDIO_JSON);
  const studioData = STUDIO_JSON;
  const [containerWidth, setContainerWidth] = useState<number>(1200);
  const chartRef = useRef<HTMLDivElement>(null);
  const hoveredChartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastSelectedCountryRef = useRef<string | null>(null);

  // 页面打点
  useEffect(() => {
    buriedPointPage({
      pageId: '208001',
      pageName: 'STH World Map',
      extInfo: '',
      pagePath: '/sth/worldmap',
      typeId: "dashboard",
    });
  }, []);

  // 获取在线的 STUDIO_JSON 数据
  // useEffect(() => {
  //   const fetchOnlineStudioData = async () => {
  //     try {
  //       const response = await getDashboardRainbowInfo({
  //         game_code: gameCode,
  //         folder: 'sth',
  //       });

  //       if (Array.isArray(response) && response.length > 0) {
  //         const { value: studioListValue } = response?.find((val) => val.key === 'studio_list') || {};
  //         if (studioListValue) {
  //           setStudioData(JSON.parse(studioListValue));
  //         }
  //       } else {
  //         // 如果返回为空或不是数组，使用本地数据
  //         setStudioData(STUDIO_JSON);
  //       }
  //     } catch (error) {
  //       // 获取失败，降级使用本地数据
  //       console.warn('Failed to fetch online studio data, using local data:', error);
  //       setStudioData(STUDIO_JSON);
  //     }
  //   };

  //   if (gameCode) {
  //     fetchOnlineStudioData();
  //   }
  // }, [gameCode]);

  // 监听容器宽度和高度变化
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width || 1200;
      setContainerWidth(width);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // 计算卡片分配 - 使用容器实际宽度和高度
  const { topCards, leftCards, rightCards, bottomCards } = useMemo(
    () => getDistribution(studioData, containerWidth),
    [studioData, containerWidth],
  );

  // 显示状态：优先使用selected，其次使用hover
  const displayStudioName = selectedStudioNames || hoveredStudioName || null;

  // 处理 hover 事件 - 使用 useCallback 优化性能
  const handleStudioHover = useCallback((studioName: string | string[]) => {
    if (Array.isArray(studioName)) {
      // 多个 studio = hover 带有 sub_studio 的卡片/icon 或 点击国家选择
      // 清除 selected 状态，设置为 hover
      setSelectedStudioNames(null);
      lastSelectedCountryRef.current = null;
      setHoveredStudioName(studioName);
    } else {
      // 单个studio = hover icon
      // 清除任何 selected 状态，转为 hover
      setSelectedStudioNames(null);
      lastSelectedCountryRef.current = null;
      setHoveredStudioName(studioName);
    }
  }, []);

  const handleStudioLeave = useCallback(() => {
    // 鼠标离开时只清除 hover，保留 selected 状态
    setHoveredStudioName(null);
  }, []);

  // 清除所有选择和 hover 状态
  const handleClearSelection = useCallback(() => {
    setSelectedStudioNames(null);
    setHoveredStudioName(null);
  }, []);

  // 获取 ECharts 实例的回调
  const handleChartReady = useCallback((instance: EChartsType) => {
    setChartInstance(instance);
    setLoading(false);
  }, []);

  // 注册卡片 ref - 使用 useCallback 优化性能
  const registerCardRef = useCallback(
    (studioName: string, element: HTMLDivElement | null) => {
      if (element) {
        cardRefsMap.current.set(studioName, element);
      } else {
        cardRefsMap.current.delete(studioName);
      }
    },
    [],
  );

  return (
    <Content>
      <Content.Header
        className="dbd-sth-world-map-header"
        title={formatMessage({ id: 'studio_tech_hub_world_map' })}
      />
      <Content.Body full className={"dbd-scroll-body dbd-sth-world-map-body"}>
        {loading && (
          <div className="dbd-sth-world-map-loading">
            <Loading />
          </div>
        )}
        <div className="dbd-sth-world-map-content" ref={containerRef} style={{ visibility: loading ? 'hidden' : 'visible' }}>
          {/* 上方卡片区域 */}
          <div className="dbd-world-map-top">
            {topCards.map((studio) => (
              <StudioCard
                key={studio.name}
                ref={(el) => registerCardRef(studio.name, el)}
                studio={studio}
                position={'top'}
                onHover={handleStudioHover}
                onLeave={handleStudioLeave}
                isSelected={displayStudioName}
              />
            ))}
          </div>

          {/* 中间区域：左侧卡片 + 地图 + 右侧卡片 */}
          <div className="dbd-world-map-center">
            {/* 左侧卡片 */}
            <div className={classnames("dbd-world-map-left", {
              'dbd-world-map-card-flex': bottomCards?.length > 0
            })}>
              {leftCards.map((studio) => (
                <StudioCard
                  key={studio.name}
                  ref={(el) => registerCardRef(studio.name, el)}
                  studio={studio}
                  position={'left'}
                  onHover={handleStudioHover}
                  onLeave={handleStudioLeave}
                  isSelected={displayStudioName}
                />
              ))}
            </div>
            {/* 中央地图 */}
            <div className="dbd-world-map-main">
              <div className="dbd-world-map-wrapper">
                <StudioMap
                  studios={studioData}
                  onIconHover={handleStudioHover}
                  onIconLeave={handleStudioLeave}
                  onChartReady={handleChartReady}
                  chartRef={chartRef}
                  hoveredChartRef={hoveredChartRef}
                  hoveredStudioName={displayStudioName}
                />
                {/* Hovered chart 层：在 mask 上方显示选中的国家 */}
                <div
                  ref={hoveredChartRef}
                  className="dbd-world-map-hovered-chart"
                  style={{ visibility: displayStudioName ? 'visible' : 'hidden' }}
                /></div>
              {
                bottomCards?.length > 0 && (
                  <div className="dbd-world-map-bottom">
                    {bottomCards.map((studio) => (
                      <StudioCard
                        key={studio.name}
                        ref={(el) => registerCardRef(studio.name, el)}
                        studio={studio}
                        position={'bottom'}
                        onHover={handleStudioHover}
                        onLeave={handleStudioLeave}
                        isSelected={displayStudioName}
                      />
                    ))}
                  </div>
                )
              }
            </div>
            {/* 右侧卡片 */}
            <div className={classnames("dbd-world-map-right", {
              'dbd-world-map-card-flex': bottomCards?.length > 0
            })}>
              {rightCards.map((studio) => (
                <StudioCard
                  key={studio.name}
                  ref={(el) => registerCardRef(studio.name, el)}
                  studio={studio}
                  position={'right'}
                  onHover={handleStudioHover}
                  onLeave={handleStudioLeave}
                  isSelected={displayStudioName}
                />
              ))}
            </div>

          </div>

          {/* 连接线 */}
          <ConnectionLine
            visible={!!displayStudioName}
            hoveredStudioName={displayStudioName}
            containerRef={containerRef}
            chartRef={chartRef}
            cardRefsMap={cardRefsMap}
            chartInstance={chartInstance}
            studios={studioData}
          />
        </div>
        {
          !!displayStudioName && (
            <div className='dbd-sth-world-map-mask' onClick={handleClearSelection}></div>
          )
        }
      </Content.Body>
    </Content>
  );
};
export default connect((state: any) => {
  return {
    gameCode: state.dashboard?.currentGame?.game_code,
  };
})(WorldMap);